import { z } from 'zod';
import { platformRoles, type PlatformRole } from '../enums.js';
import { paginationFields, type PaginatedDto } from './_pagination.js';
import { sortDirections } from './patients.js';

/**
 * Console de plateforme — les gestes, et le journal qui les retient.
 *
 * Tout ce qui est décrit ici est réservé à `super_admin` sauf les deux lectures marquées comme
 * ouvertes au support. Chaque geste est journalisé avec l'acteur, l'avant et l'après ; c'est la
 * contrepartie d'un droit qui traverse les cabinets.
 */

/* -------------------------------------------------------------------------- */
/* Suspension d'un cabinet                                                     */
/* -------------------------------------------------------------------------- */

/**
 * État de suspension, tel que le membre d'un cabinet suspendu le reçoit.
 *
 * Il voyage dans les **détails du 403** que rend toute route de cabinet : c'est la seule chose
 * qu'un cabinet suspendu obtient encore du serveur, et c'est ce qui permet à l'écran de dire
 * pourquoi plutôt que d'afficher une erreur muette.
 */
export interface OrganizationSuspensionDto {
  suspendedAt: string;
  /** Motif montré aux membres du cabinet. Jamais une note interne. */
  reason: string | null;
}

/**
 * Le motif est **obligatoire**, et long : une suspension sans motif est une panne du point de vue
 * du cabinet, et personne ne saura dire six mois plus tard pourquoi elle a été posée.
 */
export const organizationSuspendSchema = z
  .object({ reason: z.string().trim().min(10).max(500) })
  .strict();
export type OrganizationSuspendInput = z.infer<typeof organizationSuspendSchema>;

/** Ce que rend une suspension ou sa levée : de quoi rafraîchir la ligne sans la relire. */
export interface OrganizationSuspensionResultDto {
  organizationId: string;
  organizationName: string;
  suspendedAt: string | null;
  suspensionReason: string | null;
}

/* Le DTO de report d'effacement vit dans `platform-console.ts` (liste des cabinets). */

/* -------------------------------------------------------------------------- */
/* Comptes                                                                     */
/* -------------------------------------------------------------------------- */

export const platformUserSortFields = ['name', 'email', 'created', 'lastLogin'] as const;
export type PlatformUserSortField = (typeof platformUserSortFields)[number];

export const platformUserListQuerySchema = z
  .object({
    /** Recherche sur le nom, le prénom ou l'adresse. */
    search: z.string().trim().max(160).optional(),
    platformRole: z.enum(platformRoles).optional(),
    active: z.enum(['true', 'false']).optional(),
    sort: z.enum(platformUserSortFields).default('name'),
    direction: z.enum(sortDirections).default('asc'),
    ...paginationFields,
  })
  .strict();
export type PlatformUserListQuery = z.infer<typeof platformUserListQuerySchema>;

/**
 * Un compte vu depuis la console. **C'est un praticien ou un membre de son personnel**, jamais un
 * patient : les patients n'ont pas de compte dans ce produit, et la console n'ouvre aucun dossier.
 */
export interface PlatformUserDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  platformRole: PlatformRole;
  isActive: boolean;
  lastLoginAt: string | null;
  emailVerifiedAt: string | null;
  /** Nombre de cabinets où le compte est membre actif. Un nombre, jamais la liste. */
  memberships: number;
  createdAt: string;
}

export type PlatformUserListDto = PaginatedDto<PlatformUserDto>;

/** Ce que rend une désactivation ou sa levée. `revokedSessions` dit ce qui est tombé. */
export interface PlatformUserStatusDto {
  userId: string;
  email: string;
  isActive: boolean;
  /** Sessions révoquées par le geste. Zéro à la réactivation, qui n'en révoque aucune. */
  revokedSessions: number;
}

/* -------------------------------------------------------------------------- */
/* Journal des actions de plateforme                                           */
/* -------------------------------------------------------------------------- */

/**
 * Les verbes que la console écrit, et les seuls que son journal rend.
 *
 * Une liste fermée plutôt qu'un filtre libre : le journal d'audit porte aussi les gestes d'un
 * cabinet sur ses propres dossiers, et la console n'a rien à y faire. Ajouter un geste à la console
 * veut dire ajouter son verbe ici — c'est délibérément un passage obligé.
 */
export const platformAuditActions = [
  /**
   * Le rôle d'un compte a été aligné sur `PLATFORM_ADMIN_EMAILS` / `PLATFORM_SUPPORT_EMAILS` :
   * au démarrage, à une connexion, ou à la vérification d'une adresse. L'acteur journalisé est le
   * compte lui-même — aucun humain n'a agi, c'est la configuration de la machine qui décide.
   */
  'platform.role_synced',
  'platform.user_deactivated',
  'platform.user_reactivated',
  'organization.plan_changed',
  'organization.suspended',
  'organization.unsuspended',
  /**
   * Séance « Voir comme » ouverte, puis fermée avec son motif (`impersonationEndReasons`).
   * L'acteur est l'administrateur réel ; le compte regardé est l'entité ; le cabinet est celui
   * que le compte aurait à la connexion.
   */
  'platform.impersonation_started',
  'platform.impersonation_ended',
] as const;
export type PlatformAuditAction = (typeof platformAuditActions)[number];

export const platformAuditQuerySchema = z
  .object({
    action: z.enum(platformAuditActions).optional(),
    ...paginationFields,
  })
  .strict();
export type PlatformAuditQuery = z.infer<typeof platformAuditQuerySchema>;

export interface PlatformAuditEntryDto {
  id: string;
  action: string;
  /** Nul pour un geste qui n'appartient à aucun cabinet : nommer un administrateur, par exemple. */
  organizationId: string | null;
  organizationName: string | null;
  actorUserId: string | null;
  /** Adresse de l'acteur, ou `null` si son compte a été effacé depuis. */
  actorEmail: string | null;
  entityType: string | null;
  entityId: string | null;
  /**
   * L'avant et l'après du geste. Des valeurs **techniques** — une offre, un rôle, un motif de
   * suspension —, jamais une donnée personnelle : c'est le contrat de `recordAudit`.
   */
  before: unknown;
  after: unknown;
  createdAt: string;
}

export type PlatformAuditListDto = PaginatedDto<PlatformAuditEntryDto>;
